import { z } from 'zod';
import { MAX_PLAYERS } from './config';
import { boardSchema, configSchema, wordSchema } from './schema-primitives';
import { textSchema as text } from './schema-primitives';
import { rankChangeSchema } from './ranked';

/** Result types are inferred from the shared boundary schemas below. */
export type WordView = z.infer<typeof wordViewSchema>;
export type RoundPlayerResult = z.infer<typeof roundPlayerResultSchema>;
export type RoundResults = z.infer<typeof roundResultsSchema>;

/** Maximum missed words stored with a completed round; shared by scoring and validation. */
export const MISSED_WORD_LIMIT = 18;

/**
 * Deterministic standings order: score descending, then name, then id. Used by
 * scoring, the detailed archive reader and reveal presentation so every surface
 * agrees without relying on database row order.
 */
export function compareRoundPlayers(
  a: Pick<RoundPlayerResult, 'score' | 'name' | 'id'>,
  b: Pick<RoundPlayerResult, 'score' | 'name' | 'id'>,
): number {
  return b.score - a.score || a.name.localeCompare(b.name) || a.id.localeCompare(b.id);
}

/** Neutral pre-standings order: stable by participant id so scores are not spoiled. */
export function neutralPlayerOrder<T extends { id: string }>(players: readonly T[]): T[] {
  return [...players].sort((a, b) => a.id.localeCompare(b.id));
}

/** Delivery lead-in before revealAt; late clients catch up instead of delaying the room. */
export const REVEAL_LEAD_IN_MS = 500;
export const REVEAL_INTRO_MS = 500;
/** Small rounds keep an unhurried recap. */
export const REVEAL_TALLY_MIN_MS = 4_000;
/** Big rounds recap words faster rather than delaying the next Live round further. */
export const REVEAL_TALLY_MAX_MS = 12_000;

export type RevealStage = 'intro' | 'tally' | 'standings';

/** Pure presentation stage from elapsed milliseconds since the reveal start. */
export function revealStageAt(elapsedMs: number, tallyMs: number): RevealStage {
  if (elapsedMs < REVEAL_INTRO_MS) return 'intro';
  if (elapsedMs < REVEAL_INTRO_MS + tallyMs) return 'tally';
  return 'standings';
}

/** 0–1 tally progress during the tally stage; 0 before, 1 at standings. */
export function revealTallyFraction(elapsedMs: number, tallyMs: number): number {
  if (elapsedMs <= REVEAL_INTRO_MS) return 0;
  return Math.min(1, (elapsedMs - REVEAL_INTRO_MS) / tallyMs);
}

const wordViewSchema = wordSchema;

export const roundPlayerResultSchema = z.object({
  id: text,
  name: text,
  avatar: text,
  score: z.number(),
  total: z.number(),
  words: z.array(wordSchema).max(2000),
  /** A labeled Competitive bot opponent; absent for people. */
  bot: z.literal(true).optional(),
  /** Competitive only: this player's visible rank change; absent for bots and other modes. */
  rankChange: rankChangeSchema.optional(),
});

/** Canonical completed-round schema; WebSocket and REST envelopes compose this shape. */
export const roundResultsSchema = z.object({
  round: z.number(),
  roundId: text,
  config: configSchema,
  players: z.array(roundPlayerResultSchema).max(MAX_PLAYERS),
  missed: z.array(wordSchema).max(MISSED_WORD_LIMIT),
  possible: z.number(),
  found: z.number(),
  board: boardSchema,
  startedAt: z.number(),
  endedAt: z.number(),
});
